export const CHECKOUT_URL =
  'https://respiratory-therapy-visual-atlas.impultienda.ar/checkout'
export const PURCHASE_ANCHOR = '#offer'
export const META_PIXEL_ID = '1139411121985045'

export const BOX_IMAGE =
  'https://cdn.magicpatterns.com/uploads/8cNX8oEV8tpfdjSqNfFYLc/Diseno-sin-titulo-9.webp'

export interface InsideImage {
  src: string
  alt: string
}

export const insideImages: InsideImage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/g4HST85Bt9jQbHkGCifhW2/Captura-de-pantalla-2026-09-22-a-la_s_-10.30.29_p._m..png',
    alt: 'Compliance: how easily the respiratory system expands — reference page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/99bupafDPXh3iCgP5pPa2Z/Captura-de-pantalla-2026-09-22-a-la_s_-10.31.22_p._m..png',
    alt: 'Low pressure or low volume: trace the system — ventilator troubleshooting page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/foCmJBUkmZUyGpEgg1NUgk/Captura-de-pantalla-2026-09-22-a-la_s_-10.30.49_p._m..png',
    alt: 'When inspiration ends too early or too late — cycling waveform comparison page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/45RCCDvYBnK6kAV1W9WQTg/Captura-de-pantalla-2026-09-22-a-la_s_-10.30.11_p._m..png',
    alt: 'Tidal volume: volume per breath — reference page',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/vrp7tV6369kQ4CP4bYNEgC/Captura-de-pantalla-2026-09-22-a-la_s_-10.29.56_p._m..png',
    alt: 'Know the ventilator before reading the numbers — labeled ventilator page',
  },
]

export const systemBullets = [
  'Respiratory anatomy & physiology',
  'Respiratory assessment & clinical signs',
  'Oxygen therapy & respiratory equipment',
  'Mechanical ventilation & waveforms',
  'ABG, oxygenation & respiratory monitoring',
  'Airway management & respiratory patterns',
]

export type ModuleColor = 'navy' | 'green' | 'teal' | 'purple' | 'red' | 'charcoal'

export interface Module {
  number: number
  title: string
  body: string
  steps: string
  color: ModuleColor
}

export const modules: Module[] = [
  {
    number: 1,
    title: 'RESPIRATORY ANATOMY & PHYSIOLOGY VISUAL ATLAS',
    body: 'Build a clearer foundation for understanding how the respiratory system works. Explore airways, lungs, alveoli, respiratory muscles, pulmonary circulation, ventilation, perfusion, diffusion and gas exchange through organized diagrams and visual references.',
    steps: 'Understand. Connect. Review.',
    color: 'navy',
  },
  {
    number: 2,
    title: 'RESPIRATORY ASSESSMENT & CLINICAL SIGNS VISUAL ATLAS',
    body: 'Learn to recognize respiratory findings and connect them with important clinical patterns. Explore breathing patterns, work of breathing, chest movement, cough, secretions, breath sounds and respiratory assessment findings through structured visual comparisons.',
    steps: 'Observe. Assess. Recognize.',
    color: 'green',
  },
  {
    number: 3,
    title: 'OXYGEN THERAPY & RESPIRATORY EQUIPMENT VISUAL ATLAS',
    body: 'Understand commonly encountered oxygen-delivery systems and respiratory equipment more systematically. Explore nasal cannulas, masks, high-flow systems, humidification, nebulizers, aerosol equipment, suction systems and respiratory accessories through practical visual references.',
    steps: 'Identify. Compare. Understand.',
    color: 'teal',
  },
  {
    number: 4,
    title: 'MECHANICAL VENTILATION & WAVEFORMS VISUAL ATLAS',
    body: 'Make ventilator parameters, waveforms and loops easier to understand and compare. Explore ventilation modes, pressures, volumes, PEEP, flow, waveforms, loops, patient-ventilator interaction and common abnormal patterns through structured visual references.',
    steps: 'Read. Compare. Recognize.',
    color: 'purple',
  },
  {
    number: 5,
    title: 'ABG, OXYGENATION & RESPIRATORY MONITORING VISUAL ATLAS',
    body: 'Understand respiratory values and monitoring information through organized interpretation frameworks. Explore pH, PaCO₂, HCO₃⁻, PaO₂, acid-base patterns, compensation, pulse oximetry, capnography and respiratory monitoring through comparison tools and examples.',
    steps: 'Interpret. Connect. Review.',
    color: 'red',
  },
  {
    number: 6,
    title: 'AIRWAY MANAGEMENT, RESPIRATORY CONDITIONS & TROUBLESHOOTING ATLAS',
    body: 'Bring airway concepts, respiratory conditions and troubleshooting principles together. Explore artificial airways, secretion management, respiratory patterns, obstructive and restrictive processes, common conditions and structured troubleshooting references.',
    steps: 'Identify. Compare. Troubleshoot.',
    color: 'charcoal',
  },
]

export interface Bonus {
  number: number
  title: string
  subtitle: string
  body: string
  steps: string
}

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'RESPIRATORY QUICK REFERENCE DESK GUIDE™',
    subtitle: 'Essential Respiratory Information in One Organized Reference',
    body: 'Quickly review respiratory parameters, terminology, calculations, oxygen concepts, ABG values, ventilator terminology and frequently referenced respiratory information.',
    steps: 'Find. Review. Apply.',
  },
  {
    number: 2,
    title: 'MECHANICAL VENTILATION TROUBLESHOOTING GUIDE™',
    subtitle: 'Understand What Ventilator Alarms and Graphics May Be Showing',
    body: 'Organize your review of pressures, volumes, alarms, waveforms, circuits, leaks and patient-ventilator interaction using structured troubleshooting frameworks.',
    steps: 'Observe. Check. Understand.',
  },
  {
    number: 3,
    title: 'ABG INTERPRETATION WORKBOOK™',
    subtitle: 'Practice Arterial Blood Gas Interpretation Step by Step',
    body: 'Work through structured ABG examples covering pH, PaCO₂, HCO₃⁻, oxygenation, respiratory and metabolic patterns, compensation and trend interpretation.',
    steps: 'Analyze. Interpret. Practice.',
  },
  {
    number: 4,
    title: 'AIRWAY & OXYGEN DEVICE REFERENCE HANDBOOK™',
    subtitle: 'Know the Equipment and Understand Its Key Components',
    body: 'Quickly identify oxygen-delivery devices, airway equipment, respiratory interfaces, humidification systems, circuits and important equipment components.',
    steps: 'Identify. Compare. Review.',
  },
  {
    number: 5,
    title: 'RESPIRATORY CLINICAL HANDOFF & DOCUMENTATION TOOLKIT™',
    subtitle: 'Organize Respiratory Information More Clearly',
    body: 'Use structured templates for respiratory assessments, trend tracking, ventilator reviews, airway information, ABGs, equipment checks and clinical handoff preparation.',
    steps: 'Record. Organize. Communicate.',
  },
]

export interface Differentiator {
  icon: string
  title: string
  body: string
}

export const differentiators: Differentiator[] = [
  {
    icon: '🫁',
    title: 'Practical visual references',
    body: 'Find the respiratory concept, equipment or pattern you need when studying.',
  },
  {
    icon: '🔍',
    title: 'Structured learning system',
    body: 'Move from respiratory fundamentals and assessment to interpretation and pattern recognition.',
  },
  {
    icon: '⚡',
    title: '6 respiratory study areas',
    body: 'Anatomy, assessment, oxygen therapy, ventilation, monitoring, airway management and troubleshooting.',
  },
  {
    icon: '🔄',
    title: 'Ready to use again and again',
    body: 'Return to the atlas whenever you need to review a respiratory concept.',
  },
]

export const offerBullets = [
  '6 comprehensive modules',
  '5 strategic bonuses',
  'Respiratory anatomy & assessment',
  'Oxygen therapy & respiratory equipment',
  'Mechanical ventilation & waveforms',
  'ABG, oxygenation & monitoring',
  'Airway management & troubleshooting',
]

export interface Testimonial {
  name: string
  role: string
  location: string
  quote: string
}

export const testimonials: Testimonial[] = [
  {
    name: 'Rachel Donovan, 24',
    role: 'Respiratory therapy student, 2nd year',
    location: 'Columbus, United States',
    quote:
      'Ventilator waveforms were my weakest topic. Every pressure-time and flow-time graphic looked the same to me. The waveforms module puts normal and abnormal patterns side by side, and the cycling pages finally made asynchrony make sense. I stopped panicking when my preceptor pointed at the screen and asked what I was seeing.',
  },
  {
    name: 'Tyler Brooks, 27',
    role: 'Respiratory care student, clinical rotation',
    location: 'Calgary, Canada',
    quote:
      'ABGs used to take me forever. I knew the normal values but kept getting lost on compensation. The ABG workbook walks through pH, PaCO₂ and HCO₃⁻ in the same order every time. After two weeks of practice I was interpreting gases on rounds in under a minute, and my board prep scores went up noticeably.',
  },
  {
    name: 'Chloe Harrington, 22',
    role: 'Respiratory therapy student, 1st year',
    location: 'Leeds, United Kingdom',
    quote:
      'Oxygen devices confused me completely: cannula, simple mask, venturi, non-rebreather, high-flow. The equipment atlas shows each device with its components and a clear comparison. I printed the reference pages for my lab and now I can pick up any interface and explain exactly how it works.',
  },
  {
    name: 'Megan Scott, 25',
    role: 'Respiratory therapy student preparing for boards',
    location: 'Brisbane, Australia',
    quote:
      'I study on night shifts at my part-time job, so I needed something organized I could open on my phone. The quick reference desk guide has every formula, parameter and value in one place. It replaced the pile of screenshots and sticky notes I used to carry around. Revision feels structured instead of stressful.',
  },
  {
    name: 'Daniel Reyes, 29',
    role: 'EMT transitioning to respiratory therapy',
    location: 'San Antonio, United States',
    quote:
      'Coming from EMS, I understood airways in the field but mechanical ventilation felt like a different language. The ventilator troubleshooting guide traces the whole circuit step by step, from the machine to the patient. The first time a low-volume alarm went off in clinicals, I knew exactly where to start checking.',
  },
]

export interface Faq {
  q: string
  a: string
}

export const faqs: Faq[] = [
  {
    q: 'Who is this system for?',
    a: 'Respiratory therapy students, respiratory care students, healthcare students and professionals reviewing respiratory concepts.',
  },
  {
    q: 'Is this a physical product?',
    a: 'No. RESPIRATORY THERAPY VISUAL ATLAS™ is a 100% digital resource.',
  },
  {
    q: 'Is this a training course or certification?',
    a: 'No. It is an educational visual study and reference system.',
  },
  {
    q: 'Is it another theory-heavy ebook?',
    a: 'No. It focuses on organized diagrams, respiratory references, equipment comparisons, waveforms, ABGs and structured pattern-recognition tools.',
  },
  {
    q: 'What areas are included?',
    a: 'Respiratory anatomy, assessment, oxygen therapy, equipment, mechanical ventilation, ABGs, monitoring, airway management and troubleshooting.',
  },
  {
    q: 'Does this replace formal respiratory therapy training?',
    a: 'No. It is an educational study resource and does not replace formal training, supervised clinical practice, institutional protocols or professional clinical judgment.',
  },
  {
    q: 'How will I receive it?',
    a: "You'll receive immediate digital access after purchase.",
  },
  {
    q: 'What devices can I use it on?',
    a: 'Any device that opens PDF files — laptop, tablet, phone or desktop. You can also print the reference pages you use most in lab or clinicals.',
  },
  {
    q: 'Do I need previous clinical experience?',
    a: 'No. The system starts with respiratory anatomy, physiology and assessment, then moves into equipment, ventilation, ABGs and troubleshooting, so early students and more advanced learners can both use it.',
  },
  {
    q: 'How long do I keep access, and what if it is not for me?',
    a: 'Access is yours to keep and review whenever you need it. If it is not right for you, contact us within 7 days for a full refund according to the refund terms.',
  },
]

export interface PopupSale {
  name: string
  city: string
  country: string
}

export const popupSales: PopupSale[] = [
  { name: 'Emily W.', city: 'Manchester', country: 'United Kingdom' },
  { name: 'James C.', city: 'Toronto', country: 'Canada' },
  { name: 'Sophia M.', city: 'Austin', country: 'United States' },
  { name: 'Oliver B.', city: 'Melbourne', country: 'Australia' },
  { name: 'Grace T.', city: 'Leeds', country: 'United Kingdom' },
  { name: 'Ethan R.', city: 'Vancouver', country: 'Canada' },
  { name: 'Ava S.', city: 'Chicago', country: 'United States' },
  { name: 'Liam H.', city: 'Brisbane', country: 'Australia' },
  { name: 'Charlotte D.', city: 'Bristol', country: 'United Kingdom' },
  { name: 'Noah P.', city: 'Calgary', country: 'Canada' },
  { name: 'Isabella R.', city: 'Miami', country: 'United States' },
  { name: 'Jack M.', city: 'Perth', country: 'Australia' },
]
